import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { Providers } from "@/components/providers";
import { AppErrorBoundary } from "./error-boundary";
import { AppRouter } from "./router";
import "@fontsource-variable/inter";
import "@fontsource/ibm-plex-mono/400.css";
import "@fontsource/ibm-plex-mono/500.css";
import "@fontsource/ibm-plex-mono/600.css";
import "@/app/globals.css";
import "@/app/styles/tokens.css";
import "@/app/styles/base.css";
import "@/app/styles/primitives.css";
import "@/app/styles/shell.css";

const staleBundleReloadKey = "ipf-panel-stale-bundle-reload";
const staleBundlePattern =
  /(?:failed to fetch dynamically imported module|importing a module script failed|error loading dynamically imported module|failed to load module script)/i;

function errorMessage(value: unknown) {
  if (value instanceof Error) return value.message;
  if (typeof value === "string") return value;
  return "";
}

function recoverFromStaleBundle(value: unknown) {
  if (!staleBundlePattern.test(errorMessage(value))) return false;

  try {
    const previousReload = Number(
      window.sessionStorage.getItem(staleBundleReloadKey) ?? "0",
    );
    if (Date.now() - previousReload < 30_000) return false;
    window.sessionStorage.setItem(staleBundleReloadKey, String(Date.now()));
  } catch {
    // Session storage may be unavailable. The Vite event still gets one reload.
  }

  window.location.reload();
  return true;
}

window.addEventListener("vite:preloadError", (event) => {
  const preloadEvent = event as Event & { payload?: unknown };
  if (recoverFromStaleBundle(preloadEvent.payload)) event.preventDefault();
});

window.addEventListener("unhandledrejection", (event) => {
  if (recoverFromStaleBundle(event.reason)) event.preventDefault();
});

try {
  const storedTheme = window.localStorage.getItem("ipf-panel-theme");
  const resolvedTheme = storedTheme === "dark" || storedTheme === "light"
    ? storedTheme
    : window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  document.documentElement.dataset.theme = resolvedTheme;
} catch {
  document.documentElement.dataset.theme = "light";
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <AppErrorBoundary>
      <BrowserRouter>
        <Providers><AppRouter /></Providers>
      </BrowserRouter>
    </AppErrorBoundary>
  </StrictMode>,
);
